import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/hooks/use-toast';
import { useTriggerCategorizeDealItems } from '@/lib/hooks/useTriggerCategorizeDealItems';
import { productsMapState } from '@/states/products.state';
import {
  datasourceProcessedSignalAtom,
  pendingShopifyCategoryFallbackAtom,
} from '@/states/shopify-category-fallback.state';
import { useAtomValue, useStore } from 'jotai';

/**
 * Orchestrateur « catégorisation Shopify → IA en fallback ».
 *
 * Monté au niveau page (toujours présent), il réutilise le hook IA existant
 * `useTriggerCategorizeDealItems`. Quand un `datasourceProcessed` Shopify arrive
 * (signal bumpé par `useDealItems`) et qu'un fallback est armé (par l'alert
 * sidebar), il calcule les items toujours sans catégorie et les envoie à l'IA.
 *
 * Survit à la fermeture du sidebar puisqu'il vit au-dessus de lui.
 */
export function useShopifyCategoryAiFallback(dealId: string) {
  const { t } = useTranslation();
  const store = useStore();
  const signal = useAtomValue(datasourceProcessedSignalAtom);
  const { triggerCategorizeDealItems } = useTriggerCategorizeDealItems(dealId);

  const triggerRef = useRef(triggerCategorizeDealItems);
  triggerRef.current = triggerCategorizeDealItems;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (signal === 0) return;

    const pending = store.get(pendingShopifyCategoryFallbackAtom);
    if (!pending || pending.dealId !== dealId) return;

    store.set(pendingShopifyCategoryFallbackAtom, null);

    const map = store.get(productsMapState);
    const residual = pending.itemIds.filter((id) => {
      const item = map.get(id);
      return (
        item != null && !item.activeVersion.productCategory?.productCategoryId
      );
    });
    if (residual.length === 0) return;

    toast({
      description: `⏳ ${tRef.current('general.categorization_in_progress')}`,
    });
    triggerRef.current(residual, false).catch(() => {
      toast({
        title: tRef.current('general.error'),
        description: tRef.current('general.error_categorize_deal_items'),
        variant: 'destructive',
      });
    });
  }, [signal, store, dealId]);
}
